import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ChatState, Conversation, Message } from '../../types';
import { chatService } from '../../services/chatService';

// Initial state
const initialState: ChatState = {
  conversations: [],
  currentConversation: null,
  isLoading: false,
  isSending: false,
  error: null,
};

// Async thunks
export const fetchConversations = createAsyncThunk(
  'chat/fetchConversations',
  async (_, { rejectWithValue }) => {
    try {
      return await chatService.listConversations();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch conversations');
    }
  }
);

export const startNewConversation = createAsyncThunk(
  'chat/startNew',
  async (_, { rejectWithValue }) => {
    try {
      const response = await chatService.startConversation();
      return {
        conversationId: response.conversationId,
        messages: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      } as Conversation;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to start conversation');
    }
  }
);

export const loadConversation = createAsyncThunk(
  'chat/loadConversation',
  async (conversationId: string, { rejectWithValue }) => {
    try {
      return await chatService.getConversation(conversationId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to load conversation');
    }
  }
);

export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  async ({ conversationId, message }: { conversationId: string; message: string }, { rejectWithValue }) => {
    try {
      const response = await chatService.sendMessage(conversationId, message);
      return {
        userMessage: {
          role: 'user' as const,
          content: message,
          timestamp: new Date().toISOString(),
        },
        assistantMessage: {
          role: 'assistant' as const,
          content: response.response,
          timestamp: response.timestamp,
        },
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send message');
    }
  }
);

// Slice
const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentConversation: (state) => {
      state.currentConversation = null;
    },
    addOptimisticMessage: (state, action: PayloadAction<Message>) => {
      if (state.currentConversation) {
        state.currentConversation.messages.push(action.payload);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch conversations
      .addCase(fetchConversations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.conversations = action.payload;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Start new conversation
      .addCase(startNewConversation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(startNewConversation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentConversation = action.payload;
        state.conversations.unshift({
          conversationId: action.payload.conversationId,
          createdAt: action.payload.createdAt,
          updatedAt: action.payload.updatedAt,
        });
      })
      .addCase(startNewConversation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Load conversation
      .addCase(loadConversation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadConversation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentConversation = action.payload;
      })
      .addCase(loadConversation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Send message
      .addCase(sendMessage.pending, (state) => {
        state.isSending = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.isSending = false;
        if (state.currentConversation) {
          // Remove optimistic message if exists and add real messages
          const lastMessage = state.currentConversation.messages[state.currentConversation.messages.length - 1];
          if (lastMessage?.role === 'user' && lastMessage.content === action.payload.userMessage.content) {
            state.currentConversation.messages.pop();
          }
          state.currentConversation.messages.push(action.payload.userMessage);
          state.currentConversation.messages.push(action.payload.assistantMessage);
          state.currentConversation.updatedAt = action.payload.assistantMessage.timestamp;
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.isSending = false;
        state.error = action.payload as string;
        // Remove optimistic message on error
        if (state.currentConversation) {
          const lastMessage = state.currentConversation.messages[state.currentConversation.messages.length - 1];
          if (lastMessage?.role === 'user') {
            state.currentConversation.messages.pop();
          }
        }
      });
  },
});

export const { clearError, clearCurrentConversation, addOptimisticMessage } = chatSlice.actions;
export default chatSlice.reducer;
